import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Clock, FileText, Upload, PlayCircle, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtNum, rupiah, PASS_RULES, METHODS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, Spinner } from "@/components/Brand";
import { Leaderboard } from "@/components/Leaderboard";
import { Textarea } from "@/components/ui/textarea";

const PAY_LABEL = { pending: "Menunggu verifikasi admin", approved: "Pembayaran disetujui", rejected: "Pembayaran ditolak" };

const loadSnap = (cfg) => new Promise((resolve, reject) => {
  if (window.snap) return resolve(window.snap);
  const s = document.createElement("script");
  s.src = cfg.snap_js;
  s.setAttribute("data-client-key", cfg.client_key);
  s.onload = () => resolve(window.snap);
  s.onerror = () => reject(new Error("snap"));
  document.body.appendChild(s);
});

const OnlinePay = ({ pkg, onDone }) => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/payments/midtrans/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false }));
  }, []);
  if (!cfg?.enabled) return null;
  const sync = async (orderId) => {
    try {
      const { data } = await api.post(`/payments/midtrans/${orderId}/sync`);
      if (data.status === "approved") toast.success("Pembayaran berhasil. Akses paket sudah terbuka.");
      else if (data.status === "rejected") toast.error("Pembayaran gagal atau kedaluwarsa.");
      else toast.info("Pembayaran belum selesai. Selesaikan pembayaran lalu muat ulang halaman ini.");
    } catch (e) {
      toast.error(errMsg(e));
    }
    onDone();
  };
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/midtrans/create", { package_id: pkg.id });
      const snap = await loadSnap(cfg);
      snap.pay(data.token, {
        onSuccess: () => sync(data.order_id),
        onPending: () => sync(data.order_id),
        onError: () => { toast.error("Pembayaran gagal."); sync(data.order_id); },
        onClose: () => sync(data.order_id),
      });
    } catch (e) {
      toast.error(e.message === "snap" ? "Gagal memuat halaman pembayaran Midtrans" : errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3 space-y-2 rounded-lg bg-white p-4" data-testid="online-pay-box">
      <p className="text-sm font-semibold text-[#0A192F]">Bayar online — QRIS, e-wallet, virtual account, kartu</p>
      <button onClick={pay} disabled={busy} className="btn-gold flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm disabled:opacity-60" data-testid="online-pay-btn"><CreditCard className="h-4 w-4" />{busy ? "Menyiapkan..." : `Bayar ${rupiah(pkg.price)} Sekarang`}</button>
      {pkg.payment?.method === "midtrans" && pkg.payment?.status === "pending" && <button onClick={() => sync(pkg.payment.order_id)} className="w-full text-xs font-semibold text-amber-700 hover:underline" data-testid="online-pay-check-btn">Saya sudah bayar — cek status</button>}
      {!cfg.production && <p className="text-[11px] text-slate-500">Mode uji (sandbox): tidak ada uang sungguhan yang ditarik.</p>}
    </div>
  );
};

const PaymentBox = ({ pkg, settings, onDone }) => {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const pay = pkg.payment;
  const manualPending = pay?.status === "pending" && pay?.method !== "midtrans";
  const submit = async () => {
    if (!file) return toast.error("Pilih file bukti transfer");
    const fd = new FormData();
    fd.append("package_id", pkg.id);
    fd.append("note", note);
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/payments", fd);
      toast.success("Bukti transfer terkirim. Menunggu verifikasi admin.");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-5" data-testid="payment-box">
      <h3 className="font-bold text-[#0A192F]">Pembayaran paket — {rupiah(pkg.price)}</h3>
      {pay && <p className="mt-2 text-sm font-semibold" data-testid="payment-status">Status: {pay.method === "midtrans" && pay.status === "pending" ? "Menunggu pembayaran online" : PAY_LABEL[pay.status]}{pay.admin_note ? ` — ${pay.admin_note}` : ""}</p>}
      {!manualPending && <OnlinePay pkg={pkg} onDone={onDone} />}
      {!manualPending && (
        <div className="mt-3 space-y-3">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Atau transfer manual</p>
          <p className="whitespace-pre-line text-sm text-slate-700">{settings?.payment_instructions}</p>
          <p className="whitespace-pre-line rounded-md bg-white p-3 text-sm font-semibold text-[#0A192F]" data-testid="bank-info">{settings?.bank_info}</p>
          <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-amber-400 bg-white px-4 py-3 text-sm hover:bg-amber-50" data-testid="payment-proof-label">
            <Upload className="h-4 w-4 text-amber-600" />
            <span className="truncate font-semibold text-slate-700">{file ? file.name : "Pilih foto bukti transfer (JPG/PNG/WebP, maks. 5 MB)"}</span>
            <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="payment-proof-input" />
          </label>
          <Textarea placeholder="Catatan (opsional), mis. nama pengirim" value={note} onChange={(e) => setNote(e.target.value)} data-testid="payment-note-input" />
          <button onClick={submit} disabled={busy} className="btn-gold flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm disabled:opacity-60" data-testid="payment-submit-btn"><Upload className="h-4 w-4" />{busy ? "Mengirim..." : "Kirim Bukti Transfer"}</button>
          <p className="text-xs text-slate-500">Transfer manual dibuka setelah admin memverifikasi bukti transfer.</p>
        </div>
      )}
    </div>
  );
};

export default function PackageDetail() {
  const { id } = useParams();
  const { settings } = useOutletContext();
  const { user } = useAuth();
  const nav = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [err, setErr] = useState("");
  const [starting, setStarting] = useState(false);

  const load = useCallback(() => {
    api.get(`/packages/${id}`).then((r) => setPkg(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id]);
  useEffect(() => {
    load();
  }, [load, user]);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(`/exam/start/${id}`);
      nav(`/ujian/${data.session_id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setStarting(false);
    }
  };

  if (err) return <div className="mx-auto max-w-4xl px-4 py-16"><Empty text={err} /></div>;
  if (!pkg) return <Spinner />;
  const needPay = user && pkg.price > 0 && !pkg.can_start && pkg.reason.startsWith("Paket berbayar");

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <Link to="/tryout" className="text-sm font-semibold text-slate-500 hover:text-[#0A192F]">← Daftar tryout</Link>
      <div className="navy-hero mt-4 rounded-2xl p-8 text-white">
        <p className="text-xs font-bold uppercase tracking-widest text-amber-300">{pkg.category_name}</p>
        <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl" data-testid="package-title">{pkg.name}</h1>
        <p className="mt-2 text-slate-300">{pkg.description}</p>
        <div className="mt-6 flex flex-wrap gap-6 text-sm">
          <span className="flex items-center gap-2"><FileText className="h-4 w-4 text-amber-400" />{pkg.question_count} soal</span>
          <span className="flex items-center gap-2" data-testid="package-duration"><Clock className="h-4 w-4 text-amber-400" />{pkg.duration_minutes} menit</span>
          <span className="font-bold text-amber-300">{rupiah(pkg.price)}</span>
          {pkg.end_at && <span>Berakhir: {fmtDate(pkg.end_at)}</span>}
        </div>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-5">
        <div className="space-y-6 lg:col-span-3">
          <div className="card-premium p-6">
            <h2 className="font-display text-lg font-bold text-[#0A192F]">Materi ujian</h2>
            <table className="mt-4 w-full text-sm" data-testid="package-sections-table">
              <thead><tr className="border-b text-left text-xs uppercase text-slate-500"><th className="py-2">Materi</th><th>Soal</th><th>Penilaian</th><th className="text-right">Passing Grade</th></tr></thead>
              <tbody>
                {pkg.sections.map((s) => (
                  <tr key={s.subject_id} className="border-b last:border-0"><td className="py-2.5 font-semibold">{s.name}</td><td>{s.question_count}</td><td>{METHODS[s.scoring_method]}</td><td className="text-right">{fmtNum(s.passing_grade)}</td></tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-slate-500">Aturan kelulusan: {PASS_RULES[pkg.pass_rule]}{pkg.total_passing_grade != null ? ` · Passing grade total ${fmtNum(pkg.total_passing_grade)}` : ""}</p>
          </div>
          <div className="card-premium p-6">
            <h2 className="font-display text-lg font-bold text-[#0A192F]">Petunjuk pengerjaan</h2>
            <p className="mt-3 whitespace-pre-line text-sm text-slate-600" data-testid="package-instructions">{pkg.instructions || "Tidak ada petunjuk khusus."}</p>
          </div>
        </div>
        <div className="space-y-4 lg:col-span-2">
          {!user ? (
            <div className="card-premium p-6 text-center">
              <p className="text-sm text-slate-600">Login untuk mengikuti tryout ini.</p>
              <button onClick={() => nav(`/login?next=/tryout/${id}`)} className="btn-gold mt-4 w-full rounded-lg py-3" data-testid="package-login-btn">Masuk / Daftar</button>
            </div>
          ) : pkg.ongoing_session_id ? (
            <button onClick={() => nav(`/ujian/${pkg.ongoing_session_id}`)} className="btn-gold flex w-full items-center justify-center gap-2 rounded-lg py-4" data-testid="package-resume-btn"><PlayCircle className="h-5 w-5" />Lanjutkan Ujian</button>
          ) : pkg.can_start ? (
            <button onClick={start} disabled={starting} className="btn-gold flex w-full items-center justify-center gap-2 rounded-lg py-4 disabled:opacity-60" data-testid="package-start-btn"><PlayCircle className="h-5 w-5" />{starting ? "Menyiapkan..." : "Mulai Ujian"}</button>
          ) : (
            <div className="rounded-xl border bg-white p-5 text-sm font-semibold text-slate-600" data-testid="package-blocked-reason">{pkg.reason}</div>
          )}
          {needPay && <PaymentBox pkg={pkg} settings={settings} onDone={load} />}
          <p className="text-xs text-slate-500">Timer berjalan di server sejak ujian dimulai. Jika halaman tidak sengaja dimuat ulang, buka kembali paket ini untuk melanjutkan.</p>
        </div>
      </div>
      {user && <div className="mt-8"><Leaderboard packageId={id} /></div>}
    </div>
  );
}
